import React, { useEffect, useMemo, useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import {
  LossFrameDemandBarCard,
  LossFrameDemandHistoryVisualization,
} from "../stage2/Stage2ResultStage";
import {
  DollarDemandInput,
  clampDollarValue,
  formatDollarDemandDisplay,
  integerDigitCount,
  parseDollarDemandDisplay,
} from "./DollarDemandInput";
import { DemandAgainstPotChart } from "./NegotiationDonutCharts";
import { dollars } from "../formatMoney";
import {
  getCounterpartCopy,
  getNegotiationTermCopy,
  isLossFrameDesign,
  lossOfferFromDemand,
} from "../payoffFrame";

export function NegotiationFinalOfferStage() {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();

  const negotiation = game.get("negotiation") || {};
  const design = game.get("design") || {};
  const stage1 = game.get("stage1") || {};
  const stage2 = game.get("stage2") || {};
  const preDiscussionAgreement = negotiation.preDiscussionAgreement || {};
  const pieDollars = Math.max(0, Number(negotiation.pieDollars ?? 0));
  const maxOfferDollars = Number(pieDollars.toFixed(2));
  const demandVisualizationStage2 = {
    ...stage2,
    teamWinningsBeforeAdjustment: pieDollars,
  };
  const integerDigits = integerDigitCount(maxOfferDollars);
  const isClearedByInitialOffers = preDiscussionAgreement.status === "agreed";
  const lossFrame = isLossFrameDesign(design);
  const termCopy = getNegotiationTermCopy(design);
  const counterpart = getCounterpartCopy(design);
  const finalRequestLegendLabel = termCopy.term === "offer"
    ? "Your Final Requested Pay"
    : `Your Final ${termCopy.capitalizedTerm}`;

  const initialDollarAsksByPlayerId = negotiation.initialDollarAsksByPlayerId || {};
  const partner = players.find((p) => p.id !== player.id);
  const partnerId = partner?.id;
  const previousOffer = clampDollarValue(
    Number(initialDollarAsksByPlayerId?.[player.id]),
    maxOfferDollars
  );
  const partnerPreviousOffer = clampDollarValue(
    Number(initialDollarAsksByPlayerId?.[partnerId]),
    maxOfferDollars
  );
  const finalOfferDraft = parseDollarDemandDisplay(
    player.stage.get("finalOfferDraftDollars")
  );
  const initialFinalOffer = Number.isFinite(finalOfferDraft)
    ? clampDollarValue(finalOfferDraft, maxOfferDollars)
    : Number.NaN;
  const [offerInput, setOfferInput] = useState(
    formatDollarDemandDisplay(initialFinalOffer, maxOfferDollars, integerDigits)
  );
  const [demandWasAltered, setDemandWasAltered] = useState(
    player.stage.get("finalOfferDraftAltered") === true
  );
  const [showOfferError, setShowOfferError] = useState(false);
  const [isPreviousDemandsOpen, setIsPreviousDemandsOpen] = useState(true);

  const allSubmitted = useMemo(
    () => players.every((p) => p.stage.get("submit")),
    [players]
  );

  useEffect(() => {
    if (!isClearedByInitialOffers) {
      return;
    }

    player.stage.set("submit", true);
  }, [isClearedByInitialOffers, player]);

  useEffect(() => {
    if (isClearedByInitialOffers) {
      return;
    }

    const parsedOffer = parseDollarDemandDisplay(offerInput);
    if (!Number.isFinite(parsedOffer)) {
      player.stage.set("finalOfferDraftDollars", null);
      player.stage.set("finalLossOfferDraftDollars", null);
      player.set("negotiationDraftOfferDollars", null);
      return;
    }

    const canonicalDemand = clampDollarValue(parsedOffer, maxOfferDollars);
    const nextDisplay = formatDollarDemandDisplay(
      canonicalDemand,
      maxOfferDollars,
      integerDigits
    );
    if (nextDisplay !== offerInput) {
      setOfferInput(nextDisplay);
    }
    player.stage.set("finalOfferDraftDollars", canonicalDemand);
    player.stage.set(
      "finalLossOfferDraftDollars",
      lossOfferFromDemand(canonicalDemand, maxOfferDollars)
    );
    player.set("negotiationDraftOfferDollars", canonicalDemand);
  }, [integerDigits, isClearedByInitialOffers, maxOfferDollars, offerInput, player]);

  function markDemandAltered() {
    setDemandWasAltered(true);
    player.stage.set("finalOfferDraftAltered", true);
  }

  function handleOfferInputChange(nextValue) {
    markDemandAltered();
    setOfferInput(nextValue);
    if (Number.isFinite(parseDollarDemandDisplay(nextValue))) {
      setShowOfferError(false);
    }
  }

  function handleDemandVisualizationChange(nextDemand) {
    const safeDemand = clampDollarValue(nextDemand, maxOfferDollars);
    markDemandAltered();
    setOfferInput(
      formatDollarDemandDisplay(safeDemand, maxOfferDollars, integerDigits)
    );
    setShowOfferError(false);
  }

  function handleClearOffer() {
    setOfferInput(
      formatDollarDemandDisplay(Number.NaN, maxOfferDollars, integerDigits)
    );
    setDemandWasAltered(false);
    setShowOfferError(false);
    player.stage.set("finalOfferDraftDollars", null);
    player.stage.set("finalLossOfferDraftDollars", null);
    player.stage.set("finalOfferSubmittedDollars", null);
    player.stage.set("finalLossOfferSubmittedDollars", null);
    player.stage.set("finalOfferDraftAltered", false);
    player.set("negotiationDraftOfferDollars", null);
  }

  function handleSubmit(event) {
    event.preventDefault();

    const parsedOffer = parseDollarDemandDisplay(offerInput);
    if (!Number.isFinite(parsedOffer)) {
      setShowOfferError(true);
      return;
    }

    const safeFrameValue = clampDollarValue(parsedOffer, maxOfferDollars);
    const safeOffer = safeFrameValue;
    setShowOfferError(false);

    player.stage.set("finalOfferSubmittedDollars", safeOffer);
    player.stage.set(
      "finalLossOfferSubmittedDollars",
      lossOfferFromDemand(safeOffer, maxOfferDollars)
    );
    player.stage.set("submit", true);
  }

  if (isClearedByInitialOffers) {
    return (
      <div className="max-w-4xl mx-auto px-8 py-6 space-y-4">
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          {lossFrame
            ? `Your initial ${termCopy.pluralTerm} already produced a compatible split of the savings, so final submission is skipped.`
            : `Your initial ${termCopy.pluralTerm} already cleared the pie, so final submission is skipped.`}
        </div>
        {!allSubmitted ? (
          <div className="text-sm text-gray-500">
            Waiting for {counterpart.term}…
          </div>
        ) : null}
      </div>
    );
  }

  const alreadySubmitted = Boolean(player.stage.get("submit"));
  const combinedPreviousOffers = previousOffer + partnerPreviousOffer;
  const previousLeftover = Math.max(0, pieDollars - combinedPreviousOffers);
  const previousOverage = Math.max(0, combinedPreviousOffers - pieDollars);
  const parsedCurrentFrameValue = parseDollarDemandDisplay(offerInput);
  const currentFrameValue = Number.isFinite(parsedCurrentFrameValue)
    ? clampDollarValue(parsedCurrentFrameValue, maxOfferDollars)
    : Number.NaN;
  const currentFinalOffer = currentFrameValue;
  const previousNeedsRevision = previousOverage > 0;

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      <div className="space-y-4">
        <h2 className="text-4xl font-semibold text-gray-900">
          {previousOverage > 0
            ? `You may now submit your final ${termCopy.term}.`
            : "The negotiation succeeded."}
        </h2>
        <div
          className={`rounded-md border p-4 text-2xl ${
            previousNeedsRevision
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          <div>
            Combined previous {termCopy.pluralTerm}:{" "}
            <strong>{dollars(combinedPreviousOffers)}</strong>
          </div>
          {lossFrame ? (
            previousOverage > 0 ? (
              <div>
                <p>
                  The two previous {termCopy.pluralTerm} were{" "}
                  <strong>{dollars(previousOverage)}</strong> over the
                  saved amount.
                </p>
              </div>
            ) : (
              <div>
                The two previous {termCopy.pluralTerm} left{" "}
                <strong>{dollars(previousLeftover)}</strong> unclaimed.
              </div>
            )
          ) : previousOverage > 0 ? (
            <div>
              <p>
                The two previous {termCopy.pluralTerm} were{" "}
                <strong>{dollars(previousOverage)}</strong> over the pot.
              </p>
            </div>
          ) : (
            <div>
              The two previous {termCopy.pluralTerm} left{" "}
              <strong>{dollars(previousLeftover)}</strong>{" "}
              unclaimed.
            </div>
          )}
        </div>
        <section className="space-y-4">
          <h2 className="text-4xl font-semibold text-gray-900">
            <button
              type="button"
              className="flex w-full items-center justify-between rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-empirica-500"
              aria-expanded={isPreviousDemandsOpen}
              aria-controls="previous-demands-content"
              onClick={() => setIsPreviousDemandsOpen((isOpen) => !isOpen)}
            >
              <span>Previous {termCopy.pluralTerm}</span>
              <span className="ml-4 text-3xl text-gray-500" aria-hidden="true">
                {isPreviousDemandsOpen ? "−" : "+"}
              </span>
            </button>
          </h2>
          {isPreviousDemandsOpen ? (
            <div id="previous-demands-content" className="grid lg:grid-cols-2 gap-4">
              {lossFrame ? (
                <>
                  <LossFrameDemandBarCard
                    title={`Your Previous ${termCopy.capitalizedTerm}`}
                    actorLabel="You"
                    demand={previousOffer}
                    savingsAtStakeDollars={pieDollars}
                    demandLabel={termCopy.savingsLabel}
                    demandActionPast={termCopy.pastTense}
                    demandPercentLabel={termCopy.savingsPercentLabel}
                  />
                  <LossFrameDemandBarCard
                    title={`${counterpart.capitalizedTerm} Previous ${termCopy.capitalizedTerm}`}
                    actorLabel={counterpart.capitalizedTerm}
                    demand={partnerPreviousOffer}
                    savingsAtStakeDollars={pieDollars}
                    demandLabel={termCopy.savingsLabel}
                    demandActionPast={termCopy.pastTense}
                    demandPercentLabel={termCopy.savingsPercentLabel}
                  />
                </>
              ) : (
                <>
                  <DemandAgainstPotChart
                    title={`Your Previous ${termCopy.capitalizedTerm}`}
                    demand={previousOffer}
                    pot={pieDollars}
                    color="#818cf8"
                    demandLabel={termCopy.yourLabel}
                    remainingLabel="Pot Left Over"
                    overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
                    totalLabel="pot"
                    legendTextClassName="text-2xl"
                    centerValueClassName="fill-blue-700 text-base font-semibold"
                    centerTotalClassName="fill-gray-900 text-base font-semibold"
                    chartOrderLabels={[
                      "Pot Left Over",
                      termCopy.yourLabel,
                      `${termCopy.capitalizedTerm} Above Pot`,
                    ]}
                  />
                  <DemandAgainstPotChart
                    title={`${counterpart.capitalizedTerm} Previous ${termCopy.capitalizedTerm}`}
                    demand={partnerPreviousOffer}
                    pot={pieDollars}
                    color="#f87171"
                    demandLabel={termCopy.counterpartLabel}
                    remainingLabel="Pot Left Over"
                    overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
                    totalLabel="pot"
                    legendTextClassName="text-2xl"
                    centerValueClassName="fill-red-700 text-base font-semibold"
                    centerTotalClassName="fill-gray-900 text-base font-semibold"
                  />
                </>
              )}
            </div>
          ) : null}
        </section>
      </div>

      <form className="rounded-md border border-gray-200 p-4 space-y-4" onSubmit={handleSubmit}>
        <div className="flex flex-wrap items-start justify-between gap-4">
            <label htmlFor="final-offer-amount" className="block text-2xl font-medium text-gray-900">
              Final {termCopy.term} for pay you want to keep ($):
            </label>
          <Button
            type="button"
            primary
            disabled={alreadySubmitted}
            handleClick={handleClearOffer}
            className="shrink-0 text-base"
          >
            Clear {termCopy.term}
          </Button>
        </div>
        <div className="space-y-2">

          <p className="text-xl text-gray-700">
            You can navigate between the digits using the arrow keys.
          </p>
          <DollarDemandInput
            id="final-offer-amount"
            aria-describedby="final-offer-description"
            value={offerInput}
            maxDollars={maxOfferDollars}
            onChange={handleOfferInputChange}
            disabled={alreadySubmitted}
            className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-empirica-500 focus:border-empirica-500 text-2xl"
          />
        </div>

        {lossFrame ? (
          <LossFrameDemandHistoryVisualization
            stage1={stage1}
            stage2={demandVisualizationStage2}
            design={design}
            player={player}
            players={players}
            demand={currentFinalOffer}
            demandLabel={termCopy.savingsLabel}
            demandColor="#16a34a"
            demandLegendValueClassName="text-green-700"
            savingsLeftOverLabel={
              demandWasAltered ? "Potential Losses" : "Savings at Stake"
            }
            showZeroValueLegendItems={demandWasAltered}
            demandSelectionTitle={termCopy.selectionTitle}
            onDemandChange={
              alreadySubmitted ? undefined : handleDemandVisualizationChange
            }
          />
        ) : (
          <DemandAgainstPotChart
            title={`Your Final ${termCopy.capitalizedTerm}`}
            demand={currentFinalOffer}
            pot={pieDollars}
            color="#818cf8"
            demandLabel={finalRequestLegendLabel}
            remainingLabel="Pot Left Over"
            overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
            totalLabel="pot"
            legendSide
            legendTextClassName="text-2xl"
            centerValueClassName="fill-blue-700 text-base font-semibold"
            centerTotalClassName="fill-gray-900 text-base font-semibold"
            showPrimaryZeroValueLegendItems
            chartOrderLabels={[
              "Pot Left Over",
              finalRequestLegendLabel,
              `${termCopy.capitalizedTerm} Above Pot`,
            ]}
          />
        )}

        <div className="flex items-center space-x-4">
          <Button
            type="submit"
            primary
            disabled={alreadySubmitted}
            className="px-6 py-3 text-xl"
          >
            Submit final {termCopy.term}
          </Button>
          {showOfferError ? (
            <span className="text-sm text-red-600" role="alert">
              Must submit a valid {termCopy.term}.
            </span>
          ) : null}
          {alreadySubmitted && !allSubmitted ? (
            <span className="text-sm text-gray-500">
              Waiting for {counterpart.term}…
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
